/**
 * Karta pojedynczego wywolania toola (pending lub completed).
 *
 * Pokazuje **co** asystent robi, bez wnetrznosci JSON. Po zakonczeniu zwija sie
 * do jednego paska z liczba wynikow — szczegoly mozna zobaczyc rozwijajac details.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleFill, ChevronDown, ChevronRight } from 'react-bootstrap-icons';

import styles from 'theme/components/containers/ChatBase/WiseAi/ToolCallCard.module.scss';
import { resolveToolMeta, decomposeToolFunctionName } from './toolMeta';

interface ToolCallCardProps {
  functionName: string;
  status: 'pending' | 'done';
  resultSummary?: string; // np. "Znaleziono 408 produktow"
  argumentsJson?: string;
  rawResult?: string;
}

/**
 * Pretty-print JSON jezeli da sie sparsowac, inaczej zwraca oryginal.
 * Truncate gdy zbyt dluga (limit 4 KB w details).
 */
const prettifyMaybeJson = (raw: string): string => {
  try {
    const obj = JSON.parse(raw);
    const pretty = JSON.stringify(obj, null, 2);
    return pretty.length > 4000 ? `${pretty.slice(0, 4000)}\n…` : pretty;
  } catch {
    return raw.length > 4000 ? `${raw.slice(0, 4000)}\n…` : raw;
  }
};

const isEmptyJsonObject = (raw: string | undefined): boolean => {
  if (!raw) return true;
  const trimmed = raw.trim();
  return trimmed === '' || trimmed === '{}' || trimmed === '[]' || trimmed === 'null';
};

export const ToolCallCard: React.FC<ToolCallCardProps> = ({
  functionName,
  status,
  resultSummary,
  argumentsJson,
  rawResult
}) => {
  const [expanded, setExpanded] = useState(false);
  const meta = resolveToolMeta(functionName);
  const endpoint = decomposeToolFunctionName(functionName);
  const { t } = useTranslation();
  const hasArgs = !isEmptyJsonObject(argumentsJson);
  const hasResult = !isEmptyJsonObject(rawResult);
  const canExpand = status === 'done' && (hasArgs || hasResult);

  return (
    <div className={styles.card} data-status={status}>
      <button
        type="button"
        className={styles.header}
        onClick={() => canExpand && setExpanded((v) => !v)}
        disabled={!canExpand}>
        <span className={styles.statusIcon} aria-hidden>
          {status === 'pending' ? (
            <span className={styles.spinner} />
          ) : (
            <CheckCircleFill size={16} />
          )}
        </span>
        <span className={styles.label}>
          {/* Labele z toolMeta sa kluczami i18n — tlumaczymy je przez t(). */}
          {t(status === 'pending' ? meta.pendingLabel : meta.label)}
        </span>
        {status === 'done' && resultSummary && (
          <span className={styles.summary}>· {resultSummary}</span>
        )}
        {canExpand && (
          <span className={styles.chevron} aria-hidden>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </button>

      {canExpand && expanded && (
        <div className={styles.details}>
          {endpoint && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>{t('Endpoint')}</div>
              <div className={styles.endpointLine}>
                <span className={styles.methodBadge} data-method={endpoint.method}>
                  {endpoint.method}
                </span>
                <code className={styles.endpointPath}>{endpoint.path}</code>
              </div>
            </div>
          )}
          {hasArgs && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>{t('Parametry')}</div>
              <pre className={styles.pre}>
                <code>{prettifyMaybeJson(argumentsJson as string)}</code>
              </pre>
            </div>
          )}
          {hasResult && (
            <div className={styles.section}>
              <div className={styles.sectionLabel}>{t('Wynik')}</div>
              <pre className={styles.pre}>
                <code>{prettifyMaybeJson(rawResult as string)}</code>
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
